import { useState, useEffect, type CSSProperties } from 'react'
import { Link, useLocation, Outlet, useNavigate, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTheme } from '@/contexts/ThemeContext'
import { useOrgStore } from '@/stores/orgStore'
import { alertsApi } from '@/api/clients/alertsApi'
import { useAuthStore } from '@/stores/authStore'
import { useViewStore, PERSONAS } from '@/stores/viewStore'
import { useChatStore } from '@/stores/chatStore'
import { ChatDrawer } from '@/components/ai/ChatDrawer'
import { accountApi } from '@/api/clients/accountApi'
import { authApi } from '@/api/clients/authApi'
import { useEmbedStore } from '@/stores/embedStore'
import { isEmbedded, isRouteAllowed } from '@/lib/embed'

interface NavItem {
  path: string
  icon: string
  label: string
}

const SEV_HEX: Record<string, string> = { critical: '#C22221', high: '#e8734f', medium: '#eab308', info: '#94a3b8' }

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (sec < 60) return 'now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const d = Math.floor(hr / 24)
  if (d < 30) return `${d}d`
  return `${Math.floor(d / 30)}mo`
}

// Landing = prioritised asset worklist (§5.8). Sources are NOT top-level — they
// live under an inventory detail. Graph is de-emphasised — an Assets lens tab /
// row-drawer, never top-level nav (§5.8).
const navItems: NavItem[] = [
  { path: '/', icon: 'dashboard', label: 'Dashboard' },
  { path: '/assets', icon: 'shield', label: 'Prioritised Assets' },
  { path: '/inventories', icon: 'inventory_2', label: 'Inventories' },
  { path: '/sources', icon: 'dataset', label: 'Sources' },
  { path: '/policies', icon: 'gavel', label: 'Policies' },
  { path: '/standards', icon: 'rule', label: 'Standards' },
  { path: '/waivers', icon: 'verified_user', label: 'Waivers' },
  { path: '/alerts', icon: 'notifications', label: 'Alerts' },
  { path: '/reports', icon: 'summarize', label: 'Reports' },
  { path: '/connectors', icon: 'cable', label: 'Connectors' },
  { path: '/calibration', icon: 'tune', label: 'Calibration' },
]

export default function Layout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const toggleChat = useChatStore((s) => s.toggle)
  const openChat = useChatStore((s) => s.open)
  const isChatOpen = useChatStore((s) => s.isOpen)
  const chatWidth = useChatStore((s) => s.width)
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()

  // Open the assistant by default when landing on the dashboard (once per app load,
  // so it isn't forced back open after the user closes it). Not when embedded: the
  // drawer would cover the partner's frame; the assistant button still opens it.
  useEffect(() => {
    if (location.pathname === '/' && !isEmbedded()) openChat()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { currentOrgId, orgName } = useOrgStore()
  const unreadQ = useQuery({
    queryKey: ['alert-unread', currentOrgId],
    queryFn: () => alertsApi.unreadCount(currentOrgId),
    enabled: !!currentOrgId,
    retry: false,
    refetchInterval: 60_000,
  })
  const unread = unreadQ.data?.count ?? 0
  const [showAlerts, setShowAlerts] = useState(false)
  const alertsQ = useQuery({
    queryKey: ['alert-events', currentOrgId, 'header'],
    queryFn: () => alertsApi.events(currentOrgId, { limit: 8 }),
    enabled: !!currentOrgId && showAlerts,
    retry: false,
  })
  const {
    userId,
    userEmail,
    role,
    orgPlan,
    orgName: rawOrgName,
    setOrgInfo,
    setUserInfo,
    refreshToken,
    clearSession,
  } = useAuthStore()
  const navigate = useNavigate()
  const persona = useViewStore((s) => s.persona)
  const applyPersona = useViewStore((s) => s.applyPersona)
  // Embed route allowlist from the partner parent (null = unrestricted).
  const allowedRoutes = useEmbedStore((s) => s.routes)
  const routeAllowed = isRouteAllowed(location.pathname, allowedRoutes)
  const visibleNavItems = navItems.filter((item) => isRouteAllowed(item.path, allowedRoutes))

  // Exposes the current route to partner CSS, e.g. [data-qtz-route^="/reports"] [data-qtz-part="header"].
  useEffect(() => {
    document.documentElement.dataset.qtzRoute = location.pathname
  }, [location.pathname])

  const handleLogout = async () => {
    setShowUserMenu(false)
    if (refreshToken) {
      await authApi.logout(refreshToken).catch(() => {})
    }
    clearSession()
    navigate('/login', { replace: true })
  }

  useEffect(() => {
    if (!currentOrgId || rawOrgName !== null) return
    accountApi
      .getOrg(currentOrgId)
      .then((org) => setOrgInfo(org.name, org.plan))
      .catch(() => {})
  }, [currentOrgId, rawOrgName, setOrgInfo])

  useEffect(() => {
    if (!currentOrgId || !userId || userEmail !== null) return
    accountApi
      .getUser(currentOrgId, userId)
      .then((u) => setUserInfo(u.email, u.role))
      .catch(() => {})
  }, [currentOrgId, userId, userEmail, setUserInfo])

  const userInitials = userEmail ? userEmail.slice(0, 2).toUpperCase() : '?'
  const userRole = role ?? ''

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  function NavLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
    const active = isActive(item.path)
    const badge = item.path === '/alerts' && unread > 0 ? unread : 0
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${
          active
            ? 'text-primary dark:text-secondary'
            : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5 hover:text-text-light dark:hover:text-text-dark'
        }`}
      >
        <span
          className={`relative flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-colors ${
            active ? 'bg-primary dark:bg-secondary' : 'bg-transparent'
          }`}
        >
          <span
            className={`material-icons text-[18px] ${
              active ? 'text-white dark:text-neutral' : 'text-subtle-light dark:text-subtle-dark'
            }`}
          >
            {item.icon}
          </span>
          {badge > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-error text-white text-[10px] font-bold">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
        <span className="flex-1 text-sm font-medium">{item.label}</span>
        {badge > 0 && (
          <span className="text-xs font-semibold text-error">{badge > 99 ? '99+' : badge}</span>
        )}
      </Link>
    )
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        id="sidebar"
        data-qtz-part="sidebar"
        className={`fixed left-0 top-0 h-full w-72 bg-surface-dim-light dark:bg-surface-dim-dark shadow-float dark:shadow-none dark:border-r dark:border-border-dark transform transition-transform duration-300 z-50 flex flex-col ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" alt="Quantizant" className="w-8 h-8 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-text-light dark:text-text-dark tracking-display leading-tight">
                Quantizant
              </p>
              <p className="text-[11px] label-sm text-subtle-light dark:text-subtle-dark">Risk</p>
            </div>
          </div>
          <button
            id="closeSidebar"
            onClick={() => setIsSidebarOpen(false)}
            className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
          >
            <span className="material-icons text-xl">close</span>
          </button>
        </div>

        <div className="mx-4 mb-3 p-3 rounded-xl bg-container-light dark:bg-container-dark space-y-2">
          <div>
            <p className="label-sm text-subtle-light dark:text-subtle-dark mb-0.5">Organisation</p>
            <div className="flex items-center gap-2">
              <p
                className="text-sm font-semibold text-text-light dark:text-text-dark truncate"
                title={currentOrgId}
              >
                {orgName}
              </p>
              {orgPlan && (
                <span className="shrink-0 text-[10px] label-sm px-1.5 py-0.5 rounded-md bg-primary/10 text-primary">
                  {orgPlan}
                </span>
              )}
            </div>
          </div>
          {userEmail && (
            <div className="flex items-center gap-2 pt-1">
              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
                <span className="text-[10px] font-bold text-white">{userInitials}</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-text-light dark:text-text-dark truncate">
                  {userEmail}
                </p>
                {userRole && (
                  <p className="text-[11px] text-subtle-light dark:text-subtle-dark capitalize">
                    {userRole}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <nav className="px-3 flex-1 overflow-y-auto">
          <p className="label-sm text-subtle-light dark:text-subtle-dark px-2 mb-2">Navigation</p>
          <ul className="space-y-0.5">
            {visibleNavItems.map((item) => (
              <li key={item.path} data-qtz-part="nav" data-qtz-nav={item.path}>
                <NavLink item={item} onClick={() => setIsSidebarOpen(false)} />
              </li>
            ))}
          </ul>
          <div className="h-4" />
          <ul className="space-y-0.5">
            {isRouteAllowed('/settings', allowedRoutes) && (
              <li data-qtz-part="nav" data-qtz-nav="/settings">
                <NavLink
                  item={{ path: '/settings', icon: 'settings', label: 'Settings' }}
                  onClick={() => setIsSidebarOpen(false)}
                />
              </li>
            )}
          </ul>
        </nav>

        <div className="px-6 py-4">
          <p className="text-[11px] label-sm text-subtle-light dark:text-subtle-dark">
            Quantizant PQC Platform · v0.1.0
          </p>
        </div>
      </aside>

      <div
        className="min-h-screen flex flex-col transition-[padding] duration-300 lg:[padding-right:var(--chat-w,0)]"
        style={{ '--chat-w': isChatOpen ? `${chatWidth}px` : '0px' } as CSSProperties}
      >
        <header data-qtz-part="header" className="glass sticky top-0 z-30 px-6 py-3 dark:border-b dark:border-border-dark">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                id="menuToggle"
                data-qtz-part="menu-toggle"
                onClick={() => setIsSidebarOpen(true)}
                className="p-1.5 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
                aria-label="Open menu"
              >
                <span className="material-icons">menu</span>
              </button>
              <div className="flex items-center gap-2.5">
                <img data-qtz-part="brand" src="/favicon.svg" alt="Quantizant" className="w-7 h-7" />
                <h1 data-qtz-part="title" className="text-base font-semibold text-text-light dark:text-text-dark tracking-display">
                  Quantizant Risk
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Persona switch — composes a default lens over the shared view store (§5.8, P4) */}
              <label data-qtz-part="persona" className="hidden sm:flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark">
                <span className="material-icons text-[14px]">badge</span>
                <select
                  value={persona}
                  onChange={(e) => {
                    // Persona drives both the dashboard cards and the Assets lens.
                    // Only jump to Assets when a lens change actually needs that page;
                    // otherwise stay put (e.g. keep showing the persona's dashboard).
                    applyPersona(e.target.value)
                    if (location.pathname.startsWith('/assets')) navigate('/assets')
                  }}
                  className="bg-transparent border-0 focus:outline-none focus:ring-0 focus:border-0 font-medium cursor-pointer text-xs leading-tight py-0 pl-0 pr-6"
                  aria-label="Persona"
                >
                  {PERSONAS.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>

              <span data-qtz-part="org-badge" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark max-w-[200px]">
                <span className="material-icons text-[14px] shrink-0">corporate_fare</span>
                <span className="truncate font-medium" title={currentOrgId}>
                  {orgName}
                </span>
              </span>

              <button
                data-qtz-part="assistant-toggle"
                onClick={toggleChat}
                className="p-2 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
                aria-label="Open assistant"
                title="Portfolio Assistant"
              >
                <span className="material-icons text-xl">forum</span>
              </button>

              {/* Alert notifications bell + popup */}
              <div data-qtz-part="alerts" className="relative">
                <button
                  onClick={() => setShowAlerts((v) => !v)}
                  className="relative p-2 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
                  aria-label="Alerts"
                  title="Alerts"
                >
                  <span className="material-icons text-xl">notifications</span>
                  {unread > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-error text-white text-[10px] font-bold">
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </button>
                {showAlerts && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowAlerts(false)} />
                    <div className="absolute right-0 top-11 z-50 w-80 max-h-[70vh] flex flex-col bg-surface-light dark:bg-surface-dark rounded-2xl shadow-float dark:shadow-none dark:border dark:border-border-dark">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-border-light dark:border-border-dark">
                        <span className="text-sm font-semibold text-text-light dark:text-text-dark">
                          Alerts{unread > 0 ? ` · ${unread} new` : ''}
                        </span>
                        <Link
                          to="/alerts"
                          onClick={() => setShowAlerts(false)}
                          className="text-xs font-medium text-primary dark:text-secondary hover:underline"
                        >
                          View all
                        </Link>
                      </div>
                      <div className="overflow-y-auto">
                        {alertsQ.isLoading ? (
                          <p className="px-4 py-6 text-sm text-center text-subtle-light dark:text-subtle-dark">Loading…</p>
                        ) : (alertsQ.data?.length ?? 0) === 0 ? (
                          <div className="px-4 py-8 text-center">
                            <span className="material-icons text-2xl text-subtle-light dark:text-subtle-dark">notifications_off</span>
                            <p className="mt-1 text-sm text-subtle-light dark:text-subtle-dark">No active alerts</p>
                          </div>
                        ) : (
                          <ul className="divide-y divide-border-light dark:divide-border-dark">
                            {(alertsQ.data ?? []).map((e) => {
                              const title =
                                e.scope_type === 'system' || e.scope_type === 'source'
                                  ? e.scope_name || e.inventory_name || e.message
                                  : e.asset_name || e.message
                              const to =
                                e.scope_type === 'system' && e.scope_id && e.scope_name
                                  ? `/inventories/${e.scope_id}?tab=risk`
                                  : e.scope_type === 'asset' && e.asset_id
                                    ? `/assets/${e.asset_id}`
                                    : '/alerts'
                              return (
                                <li key={e.id}>
                                  <Link
                                    to={to}
                                    onClick={() => setShowAlerts(false)}
                                    className="flex items-start gap-2.5 px-4 py-2.5 hover:bg-primary/5 transition-colors"
                                  >
                                    <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: SEV_HEX[e.severity] ?? '#94a3b8' }} />
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate text-sm text-text-light dark:text-text-dark">{title}</span>
                                      <span className="block truncate text-xs text-subtle-light dark:text-subtle-dark capitalize">
                                        {e.scope_type} · {e.severity}
                                      </span>
                                    </span>
                                    <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">{timeAgo(e.created_at)}</span>
                                  </Link>
                                </li>
                              )
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <button
                data-qtz-part="theme-toggle"
                onClick={toggleTheme}
                className="p-2 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-primary/5 transition-colors"
                aria-label="Toggle theme"
              >
                <span className="material-icons text-xl">
                  {theme === 'light' ? 'dark_mode' : 'light_mode'}
                </span>
              </button>

              <div data-qtz-part="user-menu" className="relative">
                <button
                  onClick={() => setShowUserMenu((v) => !v)}
                  className="w-8 h-8 bg-primary rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
                  aria-label="User menu"
                >
                  {userInitials !== '?' ? (
                    <span className="text-xs font-bold text-white">{userInitials}</span>
                  ) : (
                    <span className="material-icons text-white text-base">person</span>
                  )}
                </button>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-10 z-50 w-64 bg-surface-light dark:bg-surface-dark rounded-2xl shadow-float dark:shadow-none dark:border dark:border-border-dark p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                          {userInitials !== '?' ? (
                            <span className="text-sm font-bold text-white">{userInitials}</span>
                          ) : (
                            <span className="material-icons text-white">person</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                            {userEmail || 'Unknown user'}
                          </p>
                          {userRole && (
                            <p className="text-xs text-subtle-light dark:text-subtle-dark capitalize">
                              {userRole}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="p-3 rounded-xl bg-container-light dark:bg-container-dark">
                        <p className="label-sm text-subtle-light dark:text-subtle-dark mb-1">
                          Organisation
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                            {orgName}
                          </p>
                          {orgPlan && (
                            <span className="shrink-0 text-[10px] label-sm px-1.5 py-0.5 rounded-md bg-primary/10 text-primary">
                              {orgPlan}
                            </span>
                          )}
                        </div>
                        <p
                          className="text-xs text-subtle-light dark:text-subtle-dark font-mono mt-1 truncate"
                          title={currentOrgId}
                        >
                          {currentOrgId}
                        </p>
                      </div>

                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-error hover:bg-error/5 rounded-xl transition-colors"
                      >
                        <span className="material-icons text-base">logout</span>
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main data-qtz-part="main" className="flex-grow p-6">
          {routeAllowed ? (
            <Outlet />
          ) : location.pathname === '/' && allowedRoutes?.length ? (
            // Dashboard not allowed — land on the first allowed route instead.
            <Navigate to={allowedRoutes[0]} replace />
          ) : (
            <div id="route-not-allowed" className="flex flex-col items-center justify-center py-24 gap-2 text-subtle-light dark:text-subtle-dark">
              <span className="material-icons text-4xl">block</span>
              <p className="text-sm">Not available in this view.</p>
            </div>
          )}
        </main>
      </div>

      <ChatDrawer />
    </div>
  )
}
